import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import generateManufacturingPDF, {
  parseActivations,
  filterInWindow,
  yyyyMmDdToYYMMDD,
  loadImageAsPng,
} from "./generateManufacturingPDF";
import fireflyLogoUrl from "../img/systems-logo.svg";
import swarmLogoUrl from "../img/swarm-logo-secondary-light.webp";
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { DateRange, Field } from '../components/ui/Field';
import SegTrack from '../components/ui/SegTrack';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';

type Brand = "firefly" | "swarm";

type Activation = { serial: string; yymmdd: string };

type LogoAsset = { dataUrl: string; ptWidth: number; ptHeight: number } | null;

const BRANDS = [
  { key: "firefly", label: "Firefly Drone Systems" },
  { key: "swarm", label: "Swarm Defense" },
] as const;

const BRAND_LOGO_URLS: Record<Brand, string> = {
  firefly: fireflyLogoUrl,
  swarm: swarmLogoUrl,
};

type Preset = {
  key: string;
  label: string;
  range: () => { from: string; to: string };
};

const PRESETS: Preset[] = [
  {
    key: "90d",
    label: "Last 90 days",
    range: () => ({
      from: dayjs().subtract(90, "day").format("YYYY-MM-DD"),
      to: dayjs().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "3m",
    label: "Last 3 months",
    range: () => ({
      from: dayjs().subtract(3, "month").startOf("month").format("YYYY-MM-DD"),
      to: dayjs().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "6m",
    label: "Last 6 months",
    range: () => ({
      from: dayjs().subtract(6, "month").startOf("month").format("YYYY-MM-DD"),
      to: dayjs().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "12m",
    label: "Last 12 months",
    range: () => ({
      from: dayjs().subtract(11, "month").startOf("month").format("YYYY-MM-DD"),
      to: dayjs().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "24m",
    label: "Last 24 months",
    range: () => ({
      from: dayjs().subtract(23, "month").startOf("month").format("YYYY-MM-DD"),
      to: dayjs().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "ytd",
    label: "Year to date",
    range: () => ({
      from: dayjs().startOf("year").format("YYYY-MM-DD"),
      to: dayjs().format("YYYY-MM-DD"),
    }),
  },
];

const toUTCStart = (yyyyMmDd: string): Date => {
  const [y, m, d] = yyyyMmDd.split("-").map((s) => parseInt(s, 10));
  return new Date(Date.UTC(y, m - 1, d));
};

const presetRange = (key: string) =>
  PRESETS.find((p) => p.key === key)?.range() ?? { from: "", to: "" };

const ManufacturingReport: React.FC = () => {
  const api = useApi();
  const toast = useToast();

  const [fromDate, setFromDate] = useState<string>(() => presetRange("12m").from);
  const [toDate, setToDate] = useState<string>(() => presetRange("12m").to);
  const [brand, setBrand] = useState<Brand>("firefly");
  const [subtitle, setSubtitle] = useState<string>("Manufacturing Report");
  const [includeMonthly, setIncludeMonthly] = useState<boolean>(true);
  const [includeWeekly, setIncludeWeekly] = useState<boolean>(true);
  const [includeSerialList, setIncludeSerialList] = useState<boolean>(false);

  const [activations, setActivations] = useState<Activation[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [logos, setLogos] = useState<Record<Brand, LogoAsset>>({
    firefly: null,
    swarm: null,
  });

  // Pre-load both brand logos as PNG dataURLs so the PDF generator can
  // stay synchronous. Failures are non-fatal — the generator falls back
  // to a text wordmark when the logo is missing.
  useEffect(() => {
    let cancelled = false;
    const load = async (key: Brand, ptWidth: number) => {
      try {
        const asset = await loadImageAsPng(BRAND_LOGO_URLS[key], ptWidth);
        if (!cancelled) setLogos((prev) => ({ ...prev, [key]: asset }));
      } catch (err) {
        console.warn(`Logo load failed for ${key}:`, err);
      }
    };
    load("firefly", 200);
    load("swarm", 200);
    return () => { cancelled = true; };
  }, []);

  const fetchActivations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/admin/manufacturing-report/activations");
      setActivations(data?.activations || []);
    } catch (err) {
      console.error("Error fetching activations:", err);
      toast.error("Failed to load activation data");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchActivations();
  }, [fetchActivations]);

  // Parse YYMMDD once per fetch — every keystroke into a date input below
  // is now a cheap string compare against this pre-parsed set.
  const parsed = useMemo(
    () => parseActivations(activations || []),
    [activations]
  );

  const inWindowCount = useMemo(
    () => filterInWindow(parsed, yyyyMmDdToYYMMDD(fromDate), yyyyMmDdToYYMMDD(toDate)).length,
    [parsed, fromDate, toDate]
  );

  const applyPreset = (key: string) => {
    const r = presetRange(key);
    if (!r.from || !r.to) return;
    setFromDate(r.from);
    setToDate(r.to);
  };

  const handleGenerate = async () => {
    if (!activations) {
      toast.error("Load activation data first");
      return;
    }
    if (!fromDate || !toDate) {
      toast.error("Pick a from and to date");
      return;
    }
    // Lexical compare: YYYY-MM-DD strings sort by date. Avoids the dayjs
    // local-time vs UTC mismatch flagged in review.
    if (fromDate > toDate) {
      toast.error("From date must be before to date");
      return;
    }
    if (!includeMonthly && !includeWeekly && !includeSerialList) {
      toast.error("Pick at least one section to include");
      return;
    }

    setGenerating(true);
    try {
      const result = generateManufacturingPDF({
        activations,
        windowStart: toUTCStart(fromDate),
        windowEnd: toUTCStart(toDate),
        brand,
        subtitle: subtitle.trim() || "Manufacturing Report",
        includeMonthly,
        includeWeekly,
        includeSerialList,
        logo: logos[brand],
      });
      toast.success(
        `PDF generated (${result.totalInWindow.toLocaleString()} drones)`
      );
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <PageLayout kind="focus" className="pt-6 text-on-surface">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">Manufacturing Report</h1>
          <p className="text-on-surface-variant text-sm">
            Generate a branded PDF summary of drones manufactured per month and
            ISO week.
          </p>
        </div>

        {/* Configure */}
        <Panel title="Configure report">
          <div className="space-y-4">

          {/* Brand */}
          <div>
            <label className="block text-xs text-on-surface-variant mb-2" id="brand-label">Brand</label>
            <SegTrack
              label="Brand"
              value={brand}
              onChange={(k) => setBrand(k as Brand)}
              options={BRANDS.map((b) => ({ key: b.key, label: b.label }))}
            />
          </div>

          {/* Date range */}
          <div>
            <label className="block text-xs text-on-surface-variant mb-2">
              Date range
            </label>
            <div
              className="flex flex-wrap gap-2 mb-3"
              role="group"
              aria-label="Date range presets"
            >
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className="bg-surface-container-highest text-on-surface px-3 py-1 rounded text-xs hover:bg-gray-750 hover:text-primary transition-all"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <DateRange
              label="Date range"
              from={fromDate}
              to={toDate}
              onChange={({ from, to }) => { setFromDate(from); setToDate(to); }}
            />
            <div
              className="text-xs text-on-surface-variant mt-3"
              role="status"
              aria-live="polite"
            >
              <FadeIn key={loading ? 'loading' : activations ? 'loaded' : 'none'}>
                {loading ? (
                  <span>Loading activations…</span>
                ) : activations ? (
                  <span>
                    <span className="text-primary font-mono">
                      {inWindowCount.toLocaleString()}
                    </span>{" "}
                    drones activated in this window (of{" "}
                    <span className="text-on-surface-variant font-mono">
                      {activations.length.toLocaleString()}
                    </span>{" "}
                    total on record).
                  </span>
                ) : (
                  <span>No data loaded.</span>
                )}
              </FadeIn>
            </div>
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-xs text-on-surface-variant mb-2">
              Subtitle (cover page)
            </label>
            <Field
              type="text"
              aria-label="Subtitle (cover page)"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Manufacturing Report"
              className="max-w-md"
            />
          </div>

          {/* Section toggles */}
          <div>
            <label className="block text-xs text-on-surface-variant mb-2">
              Sections to include
            </label>
            <div className="space-y-3">
              <Checkbox
                checked={includeMonthly}
                onChange={(e) => setIncludeMonthly(e.target.checked)}
                label="Monthly breakdown table"
                description={
                  "One row per calendar month in your window: units built, share of total, and an ASCII bar showing relative volume."
                }
              />
              <Checkbox
                checked={includeWeekly}
                onChange={(e) => setIncludeWeekly(e.target.checked)}
                label="ISO-week breakdown table"
                description={
                  "One row per ISO week (Mon-Sun): units built and an ASCII bar. Useful for spotting weekly cadence and ramp-ups."
                }
              />
              <Checkbox
                checked={includeSerialList}
                onChange={(e) => setIncludeSerialList(e.target.checked)}
                label="Serial list appendix"
                description={
                  "Every drone serial + activation date in the window, sorted oldest to newest. Page count grows with the date range."
                }
              />
            </div>
          </div>
          </div>
        </Panel>

        {/* Generate */}
        <Panel label="Generate">
          <div>
          <button
            onClick={handleGenerate}
            disabled={!activations || generating || loading}
            aria-busy={generating}
            className={buttonClass('tonal')}
          >
            <Appear show={generating}><LogoSpinner size="inline" /></Appear>
            {generating ? "Generating..." : "Generate PDF"}
          </button>
          <Appear show={loading} className="ml-3 text-xs text-on-surface-variant">
            Loading activations…
          </Appear>
          </div>
        </Panel>
      </div>
    </PageLayout>
  );
};

export default ManufacturingReport;
